import { BusinessObjectBase } from './base';
import { messageListenerSchema } from '@schemas/business-objects/message-listener';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { Id, Json, MessageListenerStatus } from '../base-types';
import type { ConditionWatcher } from './condition-watcher';
import type { PendingStateUpdate } from './pending-state-update';
import type { Timer } from './timer';

export interface MessageListenerData {
  readonly id: Id;
  readonly boundaryEventId: string;
  readonly messageName?: string;
  readonly interrupting?: boolean;
  readonly status?: MessageListenerStatus;
  readonly completedAt?: Date;
  readonly messageData?: Json;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface MessageListener extends Readonly<MessageListenerData> {}
type MessageListenerCreateWithChildrenData = Partial<MessageListenerData>;

export class MessageListener extends BusinessObjectBase {
  static readonly entityName = 'MessageListener' as const;
  static readonly plural = 'message-listeners';
  static schema = messageListenerSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<MessageListener[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new MessageListener(data));
  }

  static async findOne(filter?: any): Promise<MessageListener | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new MessageListener(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new MessageListener(data);
  }

  static async create(
    data: Omit<MessageListenerData, 'id'>,
  ): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MessageListener(result);
  }

  static async createMany(
    data: Omit<MessageListenerData, 'id'>[],
  ): Promise<MessageListener[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MessageListener(item));
  }

  static async createWithChildren(
    data: MessageListenerCreateWithChildrenData,
  ): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new MessageListener(result);
  }

  static async createManyWithChildren(
    data: MessageListenerCreateWithChildrenData[],
  ): Promise<MessageListener[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MessageListener(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<MessageListenerData>,
  ): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new MessageListener(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async trigger(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${MessageListener.plural}/${(this as any).id}/trigger`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
